/**
 * localStorageベースのポートフォリオ（シミュレーション）ストレージ
 * データベースなしのブラウザアプリ用
 */

const STORAGE_KEY = 'LOCAL_SIMULATIONS'
const ASSET_GOALS_KEY = 'LOCAL_ASSET_GOALS'
const GROWTH_SCHEDULES_KEY = 'LOCAL_GROWTH_SCHEDULES'

const DEFAULT_ASSET_GOALS = [
  { id: 1, item: 'dashboard:AssetTurnoverRatio', goal: '', present: '', last_year: '', year: 2024 },
  { id: 2, item: 'dashboard:ReturnOnEquity', goal: '', present: '', last_year: '', year: 2024 },
  { id: 3, item: 'dashboard:ReturnOnAsset', goal: '', present: '', last_year: '', year: 2024 },
  { id: 4, item: 'dashboard:BookValueperShare', goal: '', present: '', last_year: '', year: 2024 },
  { id: 5, item: 'dashboard:EarningPerShare', goal: '', present: '', last_year: '', year: 2024 },
  { id: 6, item: 'dashboard:PriceToEarningRatio', goal: '', present: '', last_year: '', year: 2024 },
  { id: 7, item: 'dashboard:PriceSaleRatio', goal: '', present: '', last_year: '', year: 2024 },
  { id: 8, item: 'dashboard:PriceToBookRatio', goal: '', present: '', last_year: '', year: 2024 },
  { id: 9, item: 'dashboard:GrossMargin', goal: '', present: '', last_year: '', year: 2024 },
  { id: 10, item: 'dashboard:ProfitMargin', goal: '', present: '', last_year: '', year: 2024 },
  { id: 11, item: 'dashboard:CashConversion', goal: '', present: '', last_year: '', year: 2024 },
  { id: 12, item: 'dashboard:RevenuePerHead', goal: '', present: '', last_year: '', year: 2024 },
  { id: 13, item: 'dashboard:EarningPerhead', goal: '', present: '', last_year: '', year: 2024 },
  { id: 14, item: 'dashboard:Institutional', goal: '', present: '', last_year: '', year: 2024 }
]

const DEFAULT_YEARS_COUNT = 10

function createDefaultGrowthSchedules(startYear) {
  const baseYear = startYear || new Date().getFullYear()
  return Array.from({ length: DEFAULT_YEARS_COUNT }, (_, i) => ({
    id: i + 1,
    year: baseYear + i,
    arr: null,
    yoy: null,
    required: null,
    raised: null,
    schedule: null,
    series: ''
  }))
}

function getSimulations() {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : []
  } catch {
    return []
  }
}

function setSimulations(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
}

function getAssetGoals(id) {
  try {
    const data = localStorage.getItem(`${ASSET_GOALS_KEY}_${id}`)
    return data ? JSON.parse(data) : DEFAULT_ASSET_GOALS.map((g, i) => ({ ...g, id: i + 1 }))
  } catch {
    return DEFAULT_ASSET_GOALS.map((g, i) => ({ ...g, id: i + 1 }))
  }
}

function setAssetGoals(id, goals) {
  localStorage.setItem(`${ASSET_GOALS_KEY}_${id}`, JSON.stringify(goals))
}

function getGrowthSchedules(id) {
  try {
    const data = localStorage.getItem(`${GROWTH_SCHEDULES_KEY}_${id}`)
    const parsed = data ? JSON.parse(data) : null
    if (Array.isArray(parsed) && parsed.length > 0) return parsed
    return createDefaultGrowthSchedules()
  } catch {
    return createDefaultGrowthSchedules()
  }
}

function setGrowthSchedules(id, schedules) {
  localStorage.setItem(`${GROWTH_SCHEDULES_KEY}_${id}`, JSON.stringify(schedules))
}

export function createPortfolioLocal(payload) {
  const list = getSimulations()
  const id = String(Date.now())
  const now = new Date().toISOString()
  const item = {
    id,
    client_name: payload.clientName || payload.client_name || 'Simulation',
    description: payload.description || '',
    created_at: now,
    role: 'COMPANY_ADMIN',
    currency: 'USD',
    time_to_ipo: null,
    goal_content: '',
    achivement_content: ''
  }
  list.push(item)
  setSimulations(list)
  setAssetGoals(id, DEFAULT_ASSET_GOALS.map((g, i) => ({ ...g, id: i + 1 })))
  setGrowthSchedules(id, createDefaultGrowthSchedules())
  return { success: true, result: item }
}

export function getPortfoliosByUserIdLocal() {
  const list = getSimulations()
  return { success: true, result: list }
}

export function getPortfolioByIdLocal({ id }) {
  const list = getSimulations()
  const item = list.find((p) => p.id === id)
  if (!item) return { success: false, result: null }
  return { success: true, result: { ...item, folder_weekly_id: item.folder_weekly_id ?? id } }
}

export function deletePortfolioLocal({ id }) {
  const list = getSimulations().filter((p) => p.id !== id)
  setSimulations(list)
  localStorage.removeItem(`${ASSET_GOALS_KEY}_${id}`)
  localStorage.removeItem(`${GROWTH_SCHEDULES_KEY}_${id}`)
  return { success: true, result: list }
}

export function updateTimeIPOLocal({ id, time }) {
  const list = getSimulations()
  const idx = list.findIndex((p) => p.id === id)
  if (idx < 0) return { success: false }
  list[idx].time_to_ipo = time
  setSimulations(list)
  return { success: true, result: list[idx] }
}

export function getAssetGoalsLocal({ id }) {
  const goals = getAssetGoals(id)
  return { success: true, result: goals }
}

export function getGrowthSchedulesLocal({ id }) {
  const schedules = getGrowthSchedules(id)
  return { success: true, result: schedules }
}

export function updateAssetGoalsLocal({ id, assetGoals, ...payload }) {
  const goals = assetGoals || payload.asset_goals || payload.assetGoals
  const current = getAssetGoals(id)
  const toSave = Array.isArray(goals) ? goals : current
  setAssetGoals(id, toSave)
  return { success: true, result: toSave }
}

export function updateGrowthSchedulesLocal({ id, growthSchedules, ...payload }) {
  const schedules = growthSchedules || payload.growth_schedules || payload.growthSchedules
  const current = getGrowthSchedules(id)
  const toSave = Array.isArray(schedules) ? schedules : current
  setGrowthSchedules(id, toSave)
  return { success: true, result: toSave }
}

export function updatePortfolioNameLocal({ id, name }) {
  const list = getSimulations()
  const idx = list.findIndex((p) => p.id === id)
  if (idx < 0) return { success: false }
  list[idx].client_name = name?.name || name
  setSimulations(list)
  return { success: true, result: list[idx] }
}

export function getAllRolesLocal() {
  return { success: true, result: { rows: [] } }
}

export function getAllMembersLocal() {
  return { success: true, result: [] }
}

export function updateCurrencyLocal({ id, currency }) {
  const list = getSimulations()
  const idx = list.findIndex((p) => p.id === id)
  if (idx < 0) return { success: false }
  list[idx].currency = currency
  setSimulations(list)
  return { success: true, result: list[idx] }
}
